import React, { useEffect, useState } from 'react';
import { FiSearch, FiZap, FiPause } from 'react-icons/fi';
import { adminApi } from '../../services/adminApi';

function AdminFinancing() {
  const [financing, setFinancing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (searchDebounced) params.search = searchDebounced;
    adminApi.getFinancing(params)
      .then((res) => {
        if (res.success) {
          setFinancing(res.data || []);
          setError(null);
        } else setError(res.message || 'Failed to load');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setFinancing([]);
      })
      .finally(() => setLoading(false));
  }, [filter, searchDebounced]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await adminApi.updateFinancingStatus(id, status);
      if (res.success) {
        setFinancing((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const formatAmount = (n) => n != null ? `${Number(n).toLocaleString()} FCFA` : '-';
  const ratingColors = { A: 'risk-a', B: 'risk-b', C: 'risk-c', D: 'risk-d', E: 'risk-e' };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Financing Activation</h1>
        <p>Activate or pause farm financing approvals</p>
      </div>
      <div className="admin-toolbar">
        <div className="admin-search">
          <FiSearch className="admin-search-icon" />
          <input
            type="search"
            placeholder="Search financing..."
            className="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filter-tabs">
          {['all', 'pending', 'active', 'paused'].map((tab) => (
            <button
              key={tab}
              className={`admin-filter-tab ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="admin-table-card">
        {loading ? (
          <div className="admin-loading" />
        ) : error ? (
          <p className="admin-error">{error}</p>
        ) : financing.length === 0 ? (
          <p className="admin-empty">No financing records found</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Farm ID</th>
                <th>Farmer</th>
                <th>Farm Details</th>
                <th>Amount</th>
                <th>Risk</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {financing.map((f) => (
                <tr key={f.id}>
                  <td><code>F{String(f.farmer_id).padStart(3, '0')}</code></td>
                  <td>{f.farmer_name}</td>
                  <td>{f.farm || `${f.crop_type || f.livestock_type || '-'} - ${f.farm_size || ''}`.trim()}</td>
                  <td>{formatAmount(f.amount)}</td>
                  <td>
                    {f.rating ? (
                      <span className={`admin-risk-badge ${ratingColors[f.rating]}`}>
                        {f.rating}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <span className={`admin-badge admin-badge-${f.status}`}>
                      {f.status}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      {f.status === 'pending' && (
                        <button
                          className="admin-btn-sm admin-btn-success"
                          title="Activate"
                          onClick={() => handleStatus(f.id, 'active')}
                          disabled={updating === f.id}
                        >
                          <FiZap /> Activate
                        </button>
                      )}
                      {f.status === 'active' && (
                        <button
                          className="admin-btn-sm admin-btn-warning"
                          title="Pause"
                          onClick={() => handleStatus(f.id, 'paused')}
                          disabled={updating === f.id}
                        >
                          <FiPause /> Pause
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminFinancing;
